import React, { PureComponent } from 'react';
import { withTranslation, TFunction } from 'react-i18next';
import styled, { css } from 'styled-components';
import LeftChevron from '@material-ui/icons/ChevronLeft';
import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';
import Location from '@solvhealth/types/interfaces/Location';
import {
  ADD_INSURANCE_ORIGIN_USER_PROFILE,
  ADD_INSURANCE_ORIGIN_APP_BOOKING,
} from '../../core/util/bookingFlowRouting';
import Button, { ButtonVariations } from '../SolvPatternLibrary/Button';
import SnackBar from '../SolvPatternLibrary/SnackBar';
import { analyticsTrackEvent } from '../../core/analytics';
import { TOUCH_ENTER_MANUALLY, TOUCH_SKIP_BACK } from '../../constants/index';
import { colors } from '../../constants/colors';
import InsuranceForm from './InsuranceForm';
import InsuranceUpload from './MobileAddInsurance';
import { fontFamily } from '../../constants/text';
import { OriginType } from '../../core/util/bookingFlowRoutingTypes';
import history from '../../core/history';
import { isPhotoOnlyInsuranceFlow } from '../../core/util/location';
import { EN, SupportedLocale } from '../../core/util/localizedStr';
import { getBooleanFlag } from '~/core/launch-darkly/flags';

const StyledTitle = styled.div.withConfig({ componentId: 'sc-ia1r67-0' })`
  color: ${colors.squid};
  font-family: ${fontFamily.bold};
  text-align: center;
  margin: 24px 24px
    ${(props) =>
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'sc' does not exist on type 'ThemedStyled... Remove this comment to see the full error message
      props.sc && props.sc.withoutSubtitle ? '15px' : '0'};
  font-size: 18px;
  letter-spacing: 0.62px;
  line-height: 28px;
`;

const SubTitle = styled.div.withConfig({ componentId: 'sc-ia1r67-1' })`
  color: #5b6b7d;
  font-family: ${fontFamily.book};
  font-size: 14px;
  font-weight: 300;
  letter-spacing: -0.2px;
  line-height: 20px;
  text-align: center;
  margin-left: 24px;
  margin-right: 24px;
`;

const Anchor = styled(Grid).withConfig({ componentId: 'sc-ia1r67-2' })`
  width: 100vw;
  overflow: hidden;
`;

const gridAreaWithBack = css`
  grid-template-areas: 'back title blank' 'back title blank';
`;

const gridAreaOnlyTitle = css`
  grid-template-areas: 'title title title';
`;

const TopContainer = styled.div.withConfig({ componentId: 'sc-ia1r67-3' })`
  display: grid;
  grid-template-columns: 20% 60% 20%;
  ${(props) =>
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'sc' does not exist on type 'ThemedStyled... Remove this comment to see the full error message
    props.sc.showBack ? gridAreaWithBack : gridAreaOnlyTitle};
  align-items: center;
`;

const ChevronLeft = styled(LeftChevron).withConfig({ componentId: 'sc-ia1r67-4' })`
  color: ${colors.crunchBerry};
`;

const TitleContainer = styled.div.withConfig({ componentId: 'sc-ia1r67-5' })`
  grid-area: title;
`;

const GoBackButton = styled(Button).withConfig({ componentId: 'sc-ia1r67-6' })`
  grid-area: back;
  color: ${colors.crunchBerry};
`;

const FormContainer = styled.div.withConfig({ componentId: 'sc-ia1r67-7' })`
  display: inline-block;
  width: 100vw;
  vertical-align: top;
`;

const defaultState = {
  showForm: false,
  skipBack: false,
  submitSuccess: false,
};

type Props = {
  handleSkip: () => void;
  hideForm?: (...args: any[]) => any;
  // @ts-expect-error ts-migrate(2749) FIXME: 'InsuranceCardShape' refers to a value, but is bei... Remove this comment to see the full error message
  insuranceCard?: InsuranceCardShape;
  clearCardData?: (...args: any[]) => any;
  addInsuranceFlow?: string;
  location: Location;
  origin: OriginType;
  handleFileChange: (...args: any[]) => any;
  onClickFront: (...args: any[]) => any;
  onClickBack: (...args: any[]) => any;
  locale?: SupportedLocale;
  t: TFunction<['addInsurance', 'common']>;
  isFamilyVisitActivatedInBookingWidget: boolean;
  primaryPatientFullName: string;
};

type State = any;

class InsuranceController extends PureComponent<Props, State> {
  state = defaultState;

  componentDidMount = () => {
    (this.showForm() || this.scanCompleted()) && this.setState({ showForm: true });
  };

  componentDidUpdate = ({ insuranceCard: { front, back } }: any) => {
    const {
      insuranceCard: { front: newFront, back: newBack },
    } = this.props;
    const { skipBack } = this.state;
    if (skipBack && !front.scanCompleted && newFront.scanCompleted) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ ...defaultState, showForm: true });
    }

    if (
      !(front.scanCompleted && back.scanCompleted) &&
      newFront.scanCompleted &&
      newBack.scanCompleted
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ ...defaultState, showForm: true });
    }
  };

  showForm = () => {
    const { location, origin } = this.props;
    return (
      location.isInsurancePhotoUploadAndOcrDisabled && origin !== ADD_INSURANCE_ORIGIN_USER_PROFILE
    );
  };

  handleEnterManually = ({ from }: any = {}) => {
    const { addInsuranceFlow } = this.props;
    analyticsTrackEvent(TOUCH_ENTER_MANUALLY, { addInsuranceFlow, from });
    this.setState({ showForm: true });
  };

  handleSkipBack = () => {
    const {
      insuranceCard: { front },
      addInsuranceFlow,
      handleSkip,
      location,
    } = this.props;
    analyticsTrackEvent(TOUCH_SKIP_BACK, { addInsuranceFlow });

    if (isPhotoOnlyInsuranceFlow(location)) {
      handleSkip();
      return;
    }

    if (front.scanCompleted) {
      this.setState({ skipBack: true, showForm: true });
      return;
    }

    this.setState({ skipBack: true });
  };

  scanStarted = () => {
    const {
      insuranceCard: { front, back },
    } = this.props;
    const { skipBack } = this.state;
    return Boolean((skipBack && front.scanStarted) || (front.scanStarted && back.scanStarted));
  };

  scanCompleted = () => {
    const {
      insuranceCard: { front, back },
    } = this.props;
    const { skipBack } = this.state;
    return Boolean(
      (skipBack && front.scanCompleted) || (front.scanCompleted && back.scanCompleted)
    );
  };

  isAddFromAccountFlow = () => this.props.addInsuranceFlow === ADD_INSURANCE_ORIGIN_USER_PROFILE;

  isAddFromAppBookingFlow = () => this.props.addInsuranceFlow === ADD_INSURANCE_ORIGIN_APP_BOOKING;

  shouldShowBack = () => this.isAddFromAccountFlow() || this.isAddFromAppBookingFlow();

  getLocale = () => this.props.locale ?? EN;

  render = () => {
    const {
      t,
      handleSkip,
      isFamilyVisitActivatedInBookingWidget,
      primaryPatientFullName,
      ...childProps
    } = this.props;
    const { showForm, skipBack } = this.state;
    const locale = this.getLocale();
    const isMobileFlow = true;

    return (
      <Grid container justify="center">
        <Grid item xs={12}>
          {/* @ts-expect-error ts-migrate(2769) FIXME: Property 'sc' does not exist on type 'IntrinsicAtt... Remove this comment to see the full error message */}
          <TopContainer sc={{ showBack: this.shouldShowBack() }}>
            {this.shouldShowBack() && (
              <GoBackButton onClick={() => history.goBack()} variation={ButtonVariations.link}>
                <ChevronLeft /> {t('common:back')}
              </GoBackButton>
            )}

            <TitleContainer>
              {!showForm && (
                <Collapse in={!showForm} timeout="auto">
                  {/* @ts-expect-error ts-migrate(2769) FIXME: Property 'sc' does not exist on type 'IntrinsicAtt... Remove this comment to see the full error message */}
                  <StyledTitle sc={{ withoutSubtitle: this.isAddFromAccountFlow() }}>
                    {this.scanStarted() &&
                      t('addInsurance:mobileAddInsuranceController.scanningYourInsurance')}
                    {!this.scanStarted() && !this.scanCompleted() && (
                      <>
                        {isFamilyVisitActivatedInBookingWidget ? (
                          <>
                            {`Add photos of ${
                              primaryPatientFullName.endsWith('s')
                                ? `${primaryPatientFullName}'`
                                : `${primaryPatientFullName}'s`
                            } insurance card`}
                          </>
                        ) : (
                          t(
                            'addInsurance:mobileAddInsuranceController.addPhotosOfYourInsuranceCard'
                          )
                        )}
                      </>
                    )}
                  </StyledTitle>
                  {!this.isAddFromAccountFlow() && (
                    <SubTitle data-testid="subtitle">
                      {t('addInsurance:mobileAddInsuranceController.willSendInsuranceToClinic')}
                    </SubTitle>
                  )}
                </Collapse>
              )}
              {showForm && (
                <Collapse in={showForm} timeout="auto">
                  {/* @ts-expect-error ts-migrate(2769) FIXME: Property 'sc' does not exist on type 'IntrinsicAtt... Remove this comment to see the full error message */}
                  <StyledTitle sc={{ withoutSubtitle: true }}>
                    <>
                      {isFamilyVisitActivatedInBookingWidget ? (
                        <>
                          {`Complete ${
                            primaryPatientFullName.endsWith('s')
                              ? `${primaryPatientFullName}'`
                              : `${primaryPatientFullName}'s`
                          } insurance details`}
                        </>
                      ) : (
                        <>
                          {t(
                            'addInsurance:mobileAddInsuranceController.completeYourInsuranceDetails'
                          )}
                        </>
                      )}
                    </>
                  </StyledTitle>
                </Collapse>
              )}
            </TitleContainer>
          </TopContainer>
        </Grid>

        <Anchor item xs={12}>
          {!showForm && (
            <Collapse in={!showForm} timeout="auto">
              <FormContainer>
                <InsuranceUpload
                  handleEnterManually={this.handleEnterManually}
                  handleSkipBack={this.handleSkipBack}
                  handleSkipInsurance={handleSkip}
                  skipBack={skipBack}
                  {...childProps}
                />
              </FormContainer>
            </Collapse>
          )}
          {showForm && (
            <Collapse in={showForm} timeout="auto">
              <FormContainer>
                <InsuranceForm isMobileFlow={isMobileFlow} locale={locale} {...this.props} />
              </FormContainer>
            </Collapse>
          )}
        </Anchor>

        <SnackBar autoHideDuration={3000} open={this.scanStarted()}>
          {t('addInsurance:mobileAddInsuranceController.scanningAndSavingPhotos')}
        </SnackBar>

        <SnackBar autoHideDuration={3000} open={this.scanCompleted()}>
          {t('addInsurance:mobileAddInsuranceController.insuranceCardPhotosSaved')}
        </SnackBar>

        <SnackBar
          autoHideDuration={3000}
          data-testid="submit-success"
          open={this.state.submitSuccess}
        >
          {this.isAddFromAccountFlow()
            ? t('addInsurance:mobileAddInsuranceController.addingInsuranceToYourAccount')
            : t('addInsurance:mobileAddInsuranceController.sendingYourInsuranceToTheClinic')}
        </SnackBar>
      </Grid>
    );
  };
}

export default withTranslation(['common', 'addInsurance'])(InsuranceController);
